import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-taobao';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Как заказывать с Taobao через YOYbuy Sheet: пошаговая инструкция для новичков и опытных покупателей',
  description: 'Подробное руководство по заказу товаров с Taobao через yoybuy spreadsheet. Как находить товары, читать отзывы, проверять продавцов и оформлять заказ без ошибок.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20photography%20of%20a%20laptop%20displaying%20an%20e-commerce%20shopping%20interface%20with%20product%20images%20on%20a%20clean%20desk%2C%20next%20to%20a%20notepad%20with%20handwritten%20shopping%20list%2C%20soft%20natural%20light%20from%20a%20large%20window%2C%20warm%20cream%20and%20beige%20tones%2C%20online%20shopping%20concept%2C%20editorial%20tech%20lifestyle%20photography%2C%20clean%20aesthetic%2C%20high%20detail%20on%20screen%20and%20paper&width=1400&height=700&seq=article-taobao-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function TaobaoArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Taobao</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Как заказывать с Taobao через YOYbuy Sheet: пошаговая инструкция для новичков и опытных покупателей
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Подробное руководство по заказу товаров с Taobao через <strong>yoybuy spreadsheet</strong>. Как находить товары, читать отзывы, проверять продавцов и оформлять заказ без ошибок.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 9 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20photography%20of%20a%20laptop%20displaying%20an%20e-commerce%20shopping%20interface%20with%20product%20images%20on%20a%20clean%20desk%2C%20next%20to%20a%20notepad%20with%20handwritten%20shopping%20list%2C%20soft%20natural%20light%20from%20a%20large%20window%2C%20warm%20cream%20and%20beige%20tones%2C%20online%20shopping%20concept%2C%20editorial%20tech%20lifestyle%20photography%2C%20clean%20aesthetic%2C%20high%20detail%20on%20screen%20and%20paper&width=1400&height=700&seq=article-taobao-hero-2026&orientation=landscape"
                  alt="Заказ с Taobao через YOYbuy Sheet"
                  title="YOYbuy Sheet Taobao — как заказывать товары с китайского маркетплейса"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Что такое Taobao и почему это лучший источник товаров
                  </h2>
                  <p>
                    Taobao — крупнейший китайский маркетплейс с миллионами продавцов и миллиардами товаров. Здесь можно найти буквально всё: от брендовой одежды до редких комплектующих, от дизайнерской мебели до коллекционных фигурок. Цены на Taobao в среднем на 40–70% ниже, чем на аналогичные товары в Европе и Америке. Но есть нюанс: платформа полностью на китайском языке, а большинство продавцов не отправляют товары за границу напрямую.
                  </p>
                  <p>
                    Именно здесь <strong>yoybuy spreadsheet</strong> становится незаменимым инструментом. Вы находите товары на Taobao, копируете ссылки в таблицу, добавляете свои требования — и агент берёт на себя всё остальное: общение с продавцом, проверку качества, консолидацию посылок и международную доставку.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как искать товары на Taobao: практические советы
                  </h2>
                  <div className="space-y-4">
                    {[
                      { tip: 'Используйте поиск по картинке', desc: 'Нашли классную вещь в Instagram или Pinterest? Загрузите фото в поиск Taobao через функцию «поиск по изображению». Это лучший способ найти точный аналог. Ссылку на найденный товар сразу копируйте в yoybuy sheet.' },
                      { tip: 'Смотрите количество заказов', desc: 'У хороших продавцов на Taobao количество выполненных заказов исчисляется сотнями и тысячами. В yoybuy spreadsheet есть колонка «Рейтинг продавца» — заполняйте её, чтобы агент понимал, у кого заказывать.' },
                      { tip: 'Читайте отзывы с фото', desc: 'Китайские покупатели часто прикрепляют фото товара к отзыву. Это золотая жила информации: вы видите реальное качество, а не студийные фото продавца. Ссылки на понравившиеся отзывы можно добавить в заметки yoybuy sheet.' },
                      { tip: 'Ищите через 1688 для опта', desc: '1688.com — оптовый аналог Taobao от той же компании Alibaba. Если вам нужно заказать 5+ одинаковых вещей, ищите на 1688. Ссылки с 1688 тоже отлично работают в yoybuy spreadsheet.' },
                    ].map((item) => (
                      <div key={item.tip} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.tip}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20a%20smartphone%20and%20tablet%20displaying%20shopping%20and%20product%20search%20interfaces%20on%20a%20light%20marble%20surface%2C%20surrounded%20by%20a%20coffee%20cup%20and%20small%20plant%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20tones%2C%20modern%20digital%20shopping%20lifestyle%2C%20editorial%20photography%2C%20clean%20aesthetic&width=1200&height=600&seq=article-taobao-search&orientation=landscape"
                    alt="YOYbuy Sheet — поиск товаров на Taobao"
                    title="Поиск товаров на Taobao через YOYbuy Spreadsheet"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Пошаговая инструкция: от ссылки на Taobao до заказа в YOYbuy Sheet
                  </h2>
                  <ol className="list-decimal pl-5 space-y-3">
                    <li><strong>Найдите товар на Taobao.</strong> Используйте поиск по ключевым словам (на английском или китайском — через переводчик), поиск по картинке или рекомендации.</li>
                    <li><strong>Скопируйте ссылку.</strong> Откройте страницу товара и скопируйте полный URL из адресной строки. Вставьте его в колонку «Ссылка» вашего <strong>yoybuy spreadsheet</strong>.</li>
                    <li><strong>Укажите параметры.</strong> В таблице заполните размер, цвет, количество, особые пожелания. Чем детальнее — тем точнее агент выполнит заказ.</li>
                    <li><strong>Укажите цену в юанях.</strong> Скопируйте цену с Taobao в колонку «Цена». <strong>Yoybuy sheet</strong> автоматически пересчитает её в вашу валюту по актуальному курсу.</li>
                    <li><strong>Добавьте несколько товаров.</strong> Повторите процесс для всех желаемых позиций. Таблица автоматически суммирует общую стоимость и примерный вес.</li>
                    <li><strong>Отправьте таблицу агенту.</strong> Готовый <strong>yoybuy spreadsheet</strong> отправляется агенту — и через несколько часов вы получаете подтверждение заказа с итоговым расчётом.</li>
                  </ol>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Какие платформы кроме Taobao поддерживает YOYbuy Sheet
                  </h2>
                  <p>
                    <strong>Yoybuy spreadsheet</strong> универсален — он работает с любыми китайскими маркетплейсами. Кроме Taobao, вы можете заказывать с Tmall (премиум-сегмент Taobao), Weidian (для реплик и уникальных вещей), 1688.com (оптовые закупки), JD.com (электроника и бытовая техника), Xianyu (рынок подержанных товаров). Все ссылки работают одинаково: копируете в таблицу, заполняете параметры, отправляете агенту. Никакой разницы в процессе.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Taobao — бесконечный источник товаров, а <strong>yoybuy spreadsheet</strong> — ваш ключ к этому источнику. Заполнили таблицу — получили товары. Всё просто, прозрачно и без языкового барьера. <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Начните заказ на главной</Link>, выбрав любую категорию. Читайте также: <Link to="/yoybuy-spreadsheet-shipping" className="text-primary-600 hover:text-primary-700 underline font-medium">гид по доставке</Link>, <Link to="/yoybuy-spreadsheet-sizes" className="text-primary-600 hover:text-primary-700 underline font-medium">таблица размеров</Link> и <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shipping" className="text-xs text-primary-600 hover:text-primary-700 underline">Доставка из Китая</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-sizes" className="text-xs text-primary-600 hover:text-primary-700 underline">Таблица размеров</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}